import { useWindowSize } from '@uidotdev/usehooks'
import type { ChartData, ChartOptions } from 'chart.js'
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'

import { useIsDarkTheme } from '@/utils/useTheme'

import { EmptyChartData } from './empty-chart-data'
import type { ToggleChartOptions } from './types'
import {
  ChartFonts,
  customToolTip,
  getTooltipCurrency,
  tooltipHideBody,
  tooltipHoursFromMinutes,
  tooltipMinutesFromSeconds,
} from './utils'

interface DoughnutChartProps {
  data: ChartData<'doughnut'>
  options?: ChartOptions<'doughnut'>
  toggleOptions?: ToggleChartOptions
  showCents?: boolean
}

ChartJS.register(ArcElement, Tooltip, Legend)

/**
 * DoughnutChart Component
 *
 * @example
 *
 * ```jsx
 * const data: ChartData<"doughnut"> = {
 *   labels: ["Jan", "Feb", "Mar"],
 *   datasets: [
 *     {
 *       label: "Dataset 1",
 *       data: [12, 19, 3],
 *       borderColor: "blue",
 *     },
 *   ],
 * };
 *
 * <DoughnutChart data={data} />
 * ```
 *
 * @param options Additional options for the chart.
 * @param data The chart data.
 */
export const DoughnutChart = ({
  data,
  options,
  toggleOptions,
  showCents,
  ...props
}: DoughnutChartProps) => {
  const isDark = useIsDarkTheme()
  const { width } = useWindowSize()

  const isMobile = (width || 0) < 640

  const ChartOptions: ChartOptions<'doughnut'> = {
    ...options,
    responsive: true,
    animation: true,
    maintainAspectRatio: false,
    aspectRatio: 1,

    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },

    elements: {
      arc: {
        borderWidth: 0,
        spacing: 0,
      },
    },

    cutout: '60%',
    plugins: {
      legend: {
        display: !toggleOptions?.hideLegend,
        align: 'center',
        position: options?.plugins?.legend?.position ?? 'right',
        fullSize: true,
        title: {
          display: toggleOptions?.showLegendTitle,
          text: toggleOptions?.LegendTitleText,
          position: 'start',
          color: ChartFonts(isDark).color,
          font: ChartFonts(isDark, isMobile).titleFont,
        },
        labels: {
          font: ChartFonts(isDark, isMobile).bodyFont,
          color: ChartFonts(isDark).color,
          pointStyle: 'circle',
          usePointStyle: true,
          boxWidth: 20,
          boxHeight: 6,
        },
      },
      tooltip: {
        ...customToolTip,
        ...(toggleOptions?.hideTooltipBody && tooltipHideBody),
        ...(toggleOptions?.isTooltipCurrency && getTooltipCurrency(showCents)),
        ...(toggleOptions?.isTooltipMinutes && tooltipHoursFromMinutes),
        ...(toggleOptions?.isTooltipSeconds && tooltipMinutesFromSeconds),
      },
    },
  }

  if (!data || (data && data.labels?.length === 0)) {
    return <EmptyChartData isFetching={false} />
  }

  const sum = data.datasets.reduce(
    (a, b) => a + b.data.reduce((a, b) => a + b, 0),
    0,
  )

  if (data.labels && data.datasets && sum === 0) {
    return (
      <div className="flex h-full shrink-0 items-center justify-center gap-6 sm:gap-0">
        <div className="h-[200px] w-[200px] shrink-0 rounded-full border-[40px] border-background" />
      </div>
    )
  }

  return (
    <Doughnut
      data={data}
      options={ChartOptions}
      {...props}
    />
  )
}
